import { useEffect, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { historyCalendarDays, localHistoryTime } from "../lib/executionHistory";

const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const inputClass = "h-10 w-full min-w-0 bg-bg border border-border px-3 text-xs leading-4 font-mono text-fg focus:border-fg/50 focus:outline-none";

/** English calendar and explicit 24-hour fields, independent of OS/browser UI. */
export function HistoryDateTimeField({ label, value, endOfDay, onChange }: {
  label: string; value: string; endOfDay?: boolean; onChange: (value: string) => void;
}): JSX.Element {
  const [date = "", time = ""] = value.split("T");
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [focusDate, setFocusDate] = useState("");
  const root = useRef<HTMLFieldSetElement>(null);
  const calendarButton = useRef<HTMLButtonElement>(null);
  const today = localHistoryTime(Date.now()).slice(0, 10);
  const cells = historyCalendarDays(month.getFullYear(), month.getMonth());
  const tabDate = cells.some(cell => cell?.date === focusDate) ? focusDate : cells.find(cell => cell)?.date;
  const defaultTime = endOfDay ? "23:59:59" : "00:00:00";
  const changeDate = (next: string) => onChange(next ? `${next}T${time || defaultTime}` : "");
  const close = () => { setOpen(false); calendarButton.current?.focus(); };
  const choose = (next: string) => { changeDate(next); close(); };

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);
  useEffect(() => {
    if (open && focusDate) root.current?.querySelector<HTMLButtonElement>(`[data-date="${focusDate}"]`)?.focus();
  }, [open, focusDate]);

  const shiftMonth = (amount: number) => {
    const next = new Date(month.getFullYear(), month.getMonth() + amount, 1);
    if (next.getFullYear() >= 1970 && next.getFullYear() <= 9999) setMonth(next);
  };

  return <fieldset ref={root} className="relative min-w-0" onKeyDown={event => {
    if (event.key === "Escape" && open) { event.preventDefault(); event.stopPropagation(); close(); }
  }} onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
  }}>
    <legend className="mb-1 p-0 text-[11px] leading-4 text-muted">{label}</legend>
    <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-2">
      <label className="min-w-0">
        <span className="sr-only">Date (YYYY-MM-DD)</span>
        <span className="flex h-10 min-w-0 border border-border bg-bg focus-within:border-fg/50">
          <input className="h-full w-full min-w-0 bg-transparent px-3 text-xs leading-4 font-mono text-fg outline-none" aria-label={`${label} date`} type="text" placeholder="YYYY-MM-DD" maxLength={10}
            value={date} onChange={event => changeDate(event.target.value)} />
          <button ref={calendarButton} className="flex w-9 shrink-0 items-center justify-center border-l border-border text-muted hover:bg-fg/5 hover:text-fg" type="button"
            aria-label={`Choose ${label.toLowerCase()} date`} aria-expanded={open} aria-haspopup="dialog" onClick={() => {
              if (open) { setOpen(false); return; }
              const selected = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T12:00:00`) : new Date();
              const start = Number.isFinite(selected.getTime()) && selected.getFullYear() >= 1970 ? selected : new Date();
              setMonth(new Date(start.getFullYear(), start.getMonth(), 1));
              setFocusDate(localHistoryTime(start.getTime()).slice(0, 10)); setOpen(true);
            }}><CalendarDays size={14} /></button>
        </span>
      </label>
      <label className="min-w-0">
        <span className="sr-only">Time (24h)</span>
        <input className={inputClass} aria-label={`${label} time`} type="text" placeholder="HH:mm:ss" maxLength={8}
          value={time} onChange={event => onChange(date || event.target.value ? `${date}T${event.target.value}` : "")} />
      </label>
    </div>
    {open && <div role="dialog" aria-label={`${label} calendar`} lang="en"
      className={`absolute ${endOfDay ? "right-0" : "left-0"} z-30 mt-2 w-[280px] max-w-[calc(100vw-3rem)] border border-border bg-bg p-3 text-fg shadow-xl`}>
      <div className="flex items-center justify-between mb-2">
        <button className="p-1 disabled:opacity-30" type="button" aria-label="Previous month" disabled={month.getFullYear() === 1970 && month.getMonth() === 0} onClick={() => shiftMonth(-1)}><ChevronLeft size={16} /></button>
        <span className="text-xs font-medium" aria-live="polite">{months[month.getMonth()]} {month.getFullYear()}</span>
        <button className="p-1 disabled:opacity-30" type="button" aria-label="Next month" disabled={month.getFullYear() === 9999 && month.getMonth() === 11} onClick={() => shiftMonth(1)}><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs">
        {weekdays.map(day => <span key={day} className="py-1 text-[10px] text-muted">{day}</span>)}
        {cells.map((cell, i) => cell ? <button key={cell.date} data-date={cell.date} type="button" tabIndex={cell.date === tabDate ? 0 : -1}
          aria-label={`${months[month.getMonth()]} ${cell.day}, ${month.getFullYear()}`} aria-pressed={cell.date === date}
          aria-current={cell.date === today ? "date" : undefined}
          className={`h-7 border ${cell.date === date ? "bg-fg text-bg border-fg" : cell.date === today ? "border-fg/40 hover:bg-fg/10" : "border-transparent hover:bg-fg/10"}`}
          onClick={() => choose(cell.date)} onKeyDown={event => {
            const offset: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
            if (offset[event.key] === undefined) return;
            event.preventDefault();
            const next = new Date(month.getFullYear(), month.getMonth(), cell.day + offset[event.key]!);
            if (next.getFullYear() < 1970 || next.getFullYear() > 9999) return;
            setMonth(new Date(next.getFullYear(), next.getMonth(), 1)); setFocusDate(localHistoryTime(next.getTime()).slice(0, 10));
          }}>{cell.day}</button> : <span key={`blank-${i}`} />)}
      </div>
      <div className="flex justify-between border-t border-border mt-2 pt-2 text-xs">
        <button type="button" className="px-1 py-1 hover:underline" onClick={() => choose(today)}>Today</button>
        <button type="button" className="px-1 py-1 hover:underline" onClick={() => { onChange(""); close(); }}>Clear</button>
      </div>
    </div>}
  </fieldset>;
}
